import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
// import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { Users, MapPin, Briefcase } from "lucide-react";
// import { useNavigate, useParams } from "react-router-dom";
import { useParams, useSearchParams } from "react-router-dom";
import { getPublicImage } from "@/utils/imageUtils";

// API URL setup
let apiUrl = "https://api.gimmefy.ai/v2";

const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

interface ParsedContent {
  case_study_description?: string;
  results_with_gimmefy?: string;
  about_the_client?: string;
  details?: string;
  testimonials?: string;
}

interface AboutClient {
  section_title?: string;
  description?: string;
  details?: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
}

interface Testimonial {
  quote?: string;
  author?: string;
  company?: string;
}

interface ResultItem {
  value?: string;
  title?: string;
  description?: string;
}

const SinglifeCaseStudyPage = () => {
  // const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug: pathSlug } = useParams<{ slug: string }>();
  const slug = searchParams.get('slug') || pathSlug;

  const [loading, setLoading] = useState(true);
  const [caseStudyData, setCaseStudyData] = useState<Record<string, unknown> | null>(null);
  const [parsedContent, setParsedContent] = useState<ParsedContent>({});
  const [results, setResults] = useState<ResultItem[]>([]);
  const [aboutClient, setAboutClient] = useState<AboutClient>({});
  const [testimonial, setTestimonial] = useState<Testimonial>({});

  useEffect(() => {
    const fetchCaseStudy = async () => {
      // Use slug from URL search params, path param, or default to singlife
      const urlSlug = slug || 'singlife-uses-gimmefy-to-power-scaled-campaigns-across-100-insurance-skus';

      try {
        setLoading(true);

        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        const params = new URLSearchParams({
          'filters[group_name]': 'MANAGE_CASE_STUDY',
          'filters[url_slug]': urlSlug,
          'sort': '-display_order'
        });

        const response = await fetch(`${apiUrl}/contents/public?${params}`, {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch case study');
        }

        const data = await response.json();
        console.log("Raw Case Study Data (Inside):", data);

        if (data.payload && Array.isArray(data.payload) && data.payload.length > 0) {
          const item = data.payload[0];
          setCaseStudyData(item);

          // Parse the JSON content string
          if (item.content && typeof item.content === 'string') {
            try {
              const parsed: ParsedContent = JSON.parse(item.content as string);
              setParsedContent(parsed);

              // Parse results_with_gimmefy
              if (parsed.results_with_gimmefy) {
                try {
                  const resultsArray = JSON.parse(parsed.results_with_gimmefy);
                  // Limit to 3 items as requested to remove test data
                  setResults(Array.isArray(resultsArray) ? resultsArray.slice(0, 3) : []);
                } catch {
                  // Error parsing results_with_gimmefy
                }
              }

              // Parse about_the_client
              if (parsed.about_the_client) {
                try {
                  const aboutClientData = JSON.parse(parsed.about_the_client);
                  setAboutClient(aboutClientData);
                } catch {
                  // Error parsing about_the_client
                }
              }

              // Parse testimonials
              if (parsed.testimonials) {
                try {
                  const testimonialData = JSON.parse(parsed.testimonials);
                  setTestimonial(testimonialData);
                } catch {
                  // Error parsing testimonials
                }
              }

              console.log("Parsed Content:", parsed);
              console.log("Results Data:", parsed.results_with_gimmefy ? JSON.parse(parsed.results_with_gimmefy) : "None");
              console.log("About Client Data:", parsed.about_the_client ? JSON.parse(parsed.about_the_client) : "None");
              console.log("Testimonials Data:", parsed.testimonials ? JSON.parse(parsed.testimonials) : "None");
            } catch {
              // Error parsing content JSON
            }
          }
        }
      } catch {
        // Error fetching case study
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudy();
  }, [slug]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-6 py-24 text-center">
          <p className="text-muted-foreground">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!caseStudyData) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-6 py-24 text-center">
          <p className="text-muted-foreground">Case study not found</p>
          {/* <Button onClick={() => navigate('/customer-stories')} className="mt-4">
            Back to Customer Stories
          </Button> */}
        </div>
      </div>
    );
  }

  const name = (caseStudyData.name as string) || '';
  const image = caseStudyData.image
    ? getPublicImage('frontend-files', caseStudyData.image as string) || "/lovable-uploads/placeholder.svg"
    : "/lovable-uploads/placeholder.svg";
  const description = parsedContent.case_study_description || '';

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Breadcrumb / Back Link */}
      <section className="bg-muted/30 border-b border-border/50">
        <div className="container mx-auto px-6 py-4">
          {/* <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/customer-stories')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Customer Stories
          </Button> */}
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="container mx-auto px-6 py-16 md:py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto"
          >
            {/* Client Logo */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-8"
            >
              <img
                src={image}
                alt="Client logo"
                className="h-12 md:h-16 object-contain"
              />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
            >
              {name}
            </motion.h1>

            {/* Subheading */}
            {description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-lg md:text-xl text-muted-foreground leading-relaxed"
              >
                {description}
              </motion.p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main Content Grid with Sidebar */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-16">
              {/* Key Results Section */}
              {(() => {
                const validResults = results.filter(stat => stat.value || stat.title || stat.description);
                return validResults.length > 0 && (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                  >
                    <h2 className="text-2xl md:text-3xl font-bold mb-8">Results With gimmefy</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {validResults.map((stat, index) => (
                        <motion.div
                          key={index}
                          variants={itemVariants}
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Card className="text-center p-8 hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30">
                            <CardContent className="p-0">
                              {stat.value && (
                                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent mb-3">
                                  {stat.value}
                                </div>
                              )}
                              {stat.title && (
                                <p className="font-semibold mb-2">{stat.title}</p>
                              )}
                              {stat.description && (
                                <p className="text-muted-foreground leading-relaxed text-sm">
                                  {stat.description}
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                );
              })()}

              {/* Quote Highlight Box */}
              {testimonial.quote && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <Card className="bg-muted/50 border-l-4 border-l-primary">
                    <CardContent className="p-8 md:p-12">
                      <blockquote className="space-y-4">
                        <p className="text-xl md:text-2xl font-medium leading-relaxed italic">
                          "{testimonial.quote}"
                        </p>
                        {testimonial.author && (
                          <footer className="text-muted-foreground font-medium">
                            — {testimonial.author}{testimonial.company ? `, ${testimonial.company}` : ''}
                          </footer>
                        )}
                      </blockquote>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Article Content */}
              {parsedContent.details && parsedContent.details.trim() && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="prose prose-lg max-w-none [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-2xl [&_blockquote]:font-medium [&_blockquote]:leading-relaxed [&_blockquote]:!mt-6 [&_blockquote]:!mb-8 [&_blockquote]:bg-muted/30 [&_blockquote]:p-6 [&_blockquote]:rounded-r-lg [&_section]:!py-0 [&_section]:!my-0 [&_p]:!text-gray-700 [&_h2]:!mt-12 [&_li]:!text-gray-700"
                  dangerouslySetInnerHTML={{ __html: parsedContent.details }}
                />
              )}
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 space-y-6">
                {aboutClient.description && (
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4">
                        {aboutClient.section_title || 'About the Client'}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {aboutClient.description}
                      </p>

                      {aboutClient.details && aboutClient.details.length > 0 && (
                        <div className="space-y-4">
                          {aboutClient.details.map((detail, index) => {
                            let IconComponent = MapPin;
                            if (detail.icon === 'team') IconComponent = Users;
                            else if (detail.icon === 'industry') IconComponent = Briefcase;

                            return (
                              <div key={index} className="flex items-start gap-3">
                                <IconComponent className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                                <div>
                                  <div className="font-medium">{detail.label}</div>
                                  <div className="text-sm text-muted-foreground">{detail.value}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterWithNewsletter />
    </div>
  );
};

export default SinglifeCaseStudyPage;
